import { AiTeamCard, AiTeamProps } from "@/shared/data/home.v2.data";
import AiTeamCardComponent from "./shared/AiTeamCard";
import SectionHeader from "./shared/SectionHeader";

/**
 * AiTeamSection — "Your AI Team that Builds, Sells & Scales"
 *
 * Dark section with SCALE eyebrow, two-tone title, dual CTAs, and a
 * 3-up grid of gradient cards (pink / blue / peach) each with a floating
 * "Launch 80% faster" style badge and a product-specific visual mockup.
 */

// Per-card gradient backgrounds (pink → blue → peach), matching the Figma.
const cardGradientClassMap: Record<AiTeamCard["kind"], string> = {
  "ai-operator":
    "bg-[radial-gradient(circle_at_50%_50%,#FFD1E8_0%,#FF85C4_65%,#D14A9A_100%)]",
  "ai-agents":
    "bg-[radial-gradient(circle_at_50%_40%,#5D8CFF_0%,#3A6BF2_70%,#2A54D4_100%)]",
  "ai-avatar":
    "bg-[radial-gradient(circle_at_50%_55%,#FFE8D0_0%,#FFCAA8_60%,#FFA978_100%)]",
};

const CardMockup = ({ kind }: { kind: AiTeamCard["kind"] }) => {
  // Lightweight stand-ins for per-kind product mockups. These get swapped
  // out for real visual components (orbit icons, chat bubbles, conversation
  // list) once the assets land — the public shape stays the same.
  if (kind === "ai-operator") {
    return (
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative h-48 w-48">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="absolute inset-0 rounded-full border border-white/40"
                style={{
                  transform: `scale(${1 - i * 0.22})`,
                  opacity: 0.6 - i * 0.15,
                }}
              />
            ))}
            <div className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gradient-to-br from-purple-3 to-brandPrimary text-white">
              <i className="fa-solid fa-brain text-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (kind === "ai-agents") {
    return (
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl p-5">
        <div className="mx-auto flex h-full w-full max-w-[240px] flex-col items-center justify-center rounded-2xl bg-white/10 p-5 backdrop-blur-sm">
          <div className="h-14 w-14 rounded-full bg-gradient-to-br from-white/60 to-white/20" />
          <div className="mt-2 font-inter text-[13px] font-semibold text-white">
            Joe Wicks
          </div>
          <div className="mt-1 rounded-full bg-white/80 px-2 py-0.5 font-inter text-[10px] text-neutralBlack">
            Speaking...
          </div>
          <div className="mt-4 ml-auto max-w-[80%] rounded-2xl bg-white px-3 py-2 font-inter text-[11px] text-neutralBlack">
            How do I get visible abs?
          </div>
        </div>
      </div>
    );
  }
  // ai-avatar — conversation list
  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-2xl p-4">
      <div className="h-full w-full rounded-2xl bg-white/80 p-3 backdrop-blur-sm">
        <div className="h-6 w-full rounded-full bg-neutralBlack/5" />
        <ul className="mt-3 space-y-2">
          {[
            { initials: "RK", name: "Robert Kramer", sub: "Custom Workout Plan" },
            { initials: "PT", name: "Purple Turtle", sub: "Intermittent Fasting" },
            { initials: "PT", name: "Parker Thompson", sub: "Endurance training" },
            { initials: "MS", name: "Megan Scott", sub: "HIIT Sessions" },
          ].map((row) => (
            <li key={row.name} className="flex items-center gap-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutralBlack/10 font-inter text-[9px] font-semibold text-neutralBlack">
                {row.initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-inter text-[11px] font-semibold text-neutralBlack">
                  {row.name}
                </div>
                <div className="truncate font-inter text-[10px] text-neutralBlack/60">
                  {row.sub}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const AiTeamSection = ({
  eyebrow,
  titleMuted,
  titleBright,
  subtitle,
  ctas,
  cards,
  image,
  imageMobile,
}: AiTeamProps) => {
  const hasOverride = Boolean(image || imageMobile);
  return (
    <section className="w-full bg-black">
      <div className="container mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <SectionHeader
          eyebrow={eyebrow}
          titleMuted={titleMuted}
          titleBright={titleBright}
          subtitle={subtitle}
          ctas={ctas}
          variant="dark-lime"
          align="center"
        />

        {hasOverride ? (
          <div className="mx-auto mt-12 max-w-6xl md:mt-16">
            {image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={image}
                alt=""
                aria-hidden="true"
                className={
                  imageMobile
                    ? "hidden h-auto w-full md:block"
                    : "block h-auto w-full"
                }
              />
            ) : null}
            {imageMobile ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={imageMobile}
                alt=""
                aria-hidden="true"
                className={
                  image
                    ? "block h-auto w-full md:hidden"
                    : "block h-auto w-full"
                }
              />
            ) : null}
          </div>
        ) : (
          <div className="mx-auto mt-12 grid max-w-6xl gap-6 md:mt-16 md:grid-cols-3">
            {cards.map((card) => (
              <AiTeamCardComponent
                key={card.kind}
                // When an SVG image is set, drop the outer gradient so the
                // image's own baked-in background reads as the visual block.
                gradientClassName={
                  card.image ? "bg-transparent" : cardGradientClassMap[card.kind]
                }
                mockup={
                  card.image ? (
                    <div className="relative aspect-square w-full overflow-hidden">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={card.image}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full object-contain"
                      />
                    </div>
                  ) : (
                    <CardMockup kind={card.kind} />
                  )
                }
                // Empty badge when image provides its own; fallback pill
                // renders only for the hardcoded-mockup path.
                badge={card.image ? "" : card.badge}
                title={card.title}
                description={card.description}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AiTeamSection;
